import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Container,
  Badge,
  Button,
} from '@mui/material';
import {
  Logout as LogoutIcon,
  Notifications as NotifIcon,
  LocalHospital as ClinicIcon,
  Home as HomeIcon,
  MonitorHeart as HealthIcon,
  SettingsOutlined as ProfileSettingsIcon,
  Assignment as ReportsIcon,
  EventAvailable as AppointmentIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import PortalSwitcher from './PortalSwitcher';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  // One account can be a clinician and a patient. Here they are the patient, so the
  // title their clinic name carries ("Dr …") is left off.
  const displayName = (user?.full_name || '').replace(/^(dr\.?|doctor)\s+/i, '');
  const [anchorEl, setAnchorEl] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    axios.get(`${API_URL}/api/v1/patient/notifications`)
      .then((res) => setUnreadCount(res.data.unread_count))
      .catch(() => {});
  }, [location.pathname]);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);
  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleSettings = () => {
    handleProfileMenuClose();
    navigate('/settings');
  };

  const navItems = [
    { label: 'Visits', icon: <HomeIcon fontSize="small" />, path: '/' },
    { label: 'Find Care', icon: <ClinicIcon fontSize="small" />, path: '/clinics' },
    { label: 'My Health', icon: <HealthIcon fontSize="small" />, path: '/health' },
    { label: 'Reports', icon: <ReportsIcon fontSize="small" />, path: '/reports' },
    { label: 'Appointments', icon: <AppointmentIcon fontSize="small" />, path: '/appointments' },
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `
          radial-gradient(ellipse 80% 50% at 50% -10%, rgba(180, 83, 9, 0.12), transparent),
          radial-gradient(ellipse 60% 40% at 100% 50%, rgba(15, 118, 110, 0.08), transparent),
          linear-gradient(180deg, #faf6f0 0%, #f5ebe0 100%)
        `,
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 253, 250, 0.88)',
          backdropFilter: 'blur(14px)',
          borderBottom: '1px solid rgba(67, 20, 7, 0.08)',
          color: 'text.primary',
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 1, sm: 2 }, py: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                component="img"
                onClick={() => navigate('/')}
                src={`${process.env.PUBLIC_URL}/scribelive-logo.png`}
                alt="ScribeLive"
                sx={{ height: 34, width: 'auto', cursor: 'pointer', display: { xs: 'none', sm: 'block' } }}
              />
              <Box
                component="img"
                onClick={() => navigate('/')}
                src={`${process.env.PUBLIC_URL}/favicon-192.png`}
                alt="ScribeLive"
                sx={{ height: 38, width: 38, cursor: 'pointer', display: { xs: 'block', sm: 'none' } }}
              />

              <Box sx={{ display: 'flex', gap: 0.5, ml: 3 }}>
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    size="small"
                    startIcon={item.icon}
                    onClick={() => navigate(item.path)}
                    sx={{
                      color: location.pathname === item.path ? 'primary.main' : 'text.secondary',
                      fontWeight: location.pathname === item.path ? 700 : 500,
                      borderBottom: location.pathname === item.path ? '2px solid' : '2px solid transparent',
                      borderRadius: 0,
                      px: 1.5,
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PortalSwitcher />
              <IconButton size="small" onClick={() => navigate('/notifications')}>
                <Badge badgeContent={unreadCount} color="error" max={9}>
                  <NotifIcon sx={{ color: location.pathname === '/notifications' ? 'primary.main' : 'text.secondary' }} />
                </Badge>
              </IconButton>
              <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' }, fontWeight: 600 }}>
                {displayName}
              </Typography>
              <IconButton onClick={handleProfileMenuOpen} size="small">
                <Avatar sx={{ width: 38, height: 38, bgcolor: 'primary.main', fontWeight: 800 }}>
                  {displayName.charAt(0)}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <ProfileSettingsIcon fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Container maxWidth="lg" sx={{ mt: 10, mb: 6, px: { xs: 2, sm: 3 } }}>
        <Outlet />
      </Container>
    </Box>
  );
}
